import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { useChat } from '../../Context/ChatContext';
import ChatThread from '../../components/Chat/ChatThread';
import PresenceAvatar from '../../components/Chat/PresenceAvatar';
import TeamSetupDialog from './TeamSetupDialog';
import { lastActiveLabel, listTime } from '../../helpers/chatTime';
import { URLS } from '../../helpers/URLS';
import './Chat.scss';

const TEAM_ICONS = {
    people: GroupsRoundedIcon,
    wallet: AccountBalanceWalletRoundedIcon,
    devices: DevicesRoundedIcon,
    shield: ShieldRoundedIcon,
    code: CodeRoundedIcon,
};

const BASE = '/api/v1/';
const get = async (url) => {
    try {
        const res = await axios.get(BASE + url);
        return res.data;
    } catch (error) {
        const payload = error && error.response && error.response.data;
        return payload && typeof payload === 'object'
            ? payload : { status: 0, success: false, message: 'Cannot reach the server' };
    }
};

const Chat = () => {
    const {
        presence, conversations, openChatWith, setActiveConversation,
        trackPresence, typingBy, connected,
    } = useChat();

    const location = useLocation();

    const [tab, setTab] = useState('teams');
    const [categories, setCategories] = useState([]);
    const [canManageTeams, setCanManageTeams] = useState(false);
    const [openTeam, setOpenTeam] = useState(null);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    const [term, setTerm] = useState('');
    const [searchResults, setSearchResults] = useState(null);

    const [active, setActive] = useState(null); // { conversationId, peer }
    const [setupOpen, setSetupOpen] = useState(false);

    /* On a narrow screen the list and the conversation cannot both be shown,
       so the page swaps between them. */
    const [mobileShowsThread, setMobileShowsThread] = useState(false);

    const loadCategories = useCallback(async () => {
        const res = await get(URLS.chatCategories);
        if (res && res.status === 200) {
            setCategories(res.data.categories || []);
            setCanManageTeams(Boolean(res.data.canManageTeams));
        }
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    const loadMembers = useCallback(async (key) => {
        setLoadingMembers(true);
        const res = await get(URLS.chatCategoryMembers(key));
        setLoadingMembers(false);
        if (res && res.status === 200) {
            setMembers(res.data.members || []);
            trackPresence((res.data.members || []).map((m) => m.employeeCode));
        } else {
            setMembers([]);
        }
    }, [trackPresence]);

    const chooseTeam = (category) => {
        setOpenTeam(category);
        setMembers([]);
        loadMembers(category.key);
    };

    /* Debounced, so a name is not searched on every keystroke. */
    const searchTimer = useRef(null);
    useEffect(() => {
        clearTimeout(searchTimer.current);
        const q = term.trim();
        if (q.length < 2) {
            setSearchResults(null);
            return undefined;
        }
        searchTimer.current = setTimeout(async () => {
            const res = await get(URLS.chatPeople(q));
            if (res && res.status === 200) {
                setSearchResults(res.data || []);
                trackPresence((res.data || []).map((m) => m.employeeCode));
            }
        }, 280);
        return () => clearTimeout(searchTimer.current);
    }, [term, trackPresence]);

    const open = useCallback(async (peer) => {
        const opened = await openChatWith(peer, { asWindow: false });
        if (!opened) return;
        setActive(opened);
        setMobileShowsThread(true);
    }, [openChatWith]);

    /* Tell the rest of the app which thread is on screen, so an arriving
       message updates the page instead of popping a window over it. */
    useEffect(() => {
        setActiveConversation(active ? active.conversationId : null);
        return () => setActiveConversation(null);
    }, [active, setActiveConversation]);

    /* Arriving from the dock's "open in Messages" button. */
    const requestedPeer = location.state && location.state.peerCode;
    const handledPeerRef = useRef(null);
    useEffect(() => {
        if (!requestedPeer || handledPeerRef.current === requestedPeer) return;
        handledPeerRef.current = requestedPeer;
        open({ employeeCode: requestedPeer });
    }, [requestedPeer, open]);

    useEffect(() => {
        const codes = conversations.map((c) => c.peer?.employeeCode).filter(Boolean);
        if (codes.length) trackPresence(codes);
    }, [conversations, trackPresence]);

    const activePeer = active?.peer;
    const activeLive = activePeer ? (presence[activePeer.employeeCode] || {}) : {};
    const activeOnline = Boolean(activeLive.online ?? activePeer?.online);

    const rows = useMemo(() => {
        if (searchResults) return searchResults;
        if (tab === 'recent') {
            return conversations.map((c) => ({
                ...c.peer,
                unread: c.unread,
                lastMessage: c.lastMessage,
                lastMessageAt: c.lastMessageAt,
            }));
        }
        return openTeam ? members : [];
    }, [searchResults, tab, conversations, openTeam, members]);

    const renderPerson = (person) => {
        const live = presence[person.employeeCode] || {};
        const online = Boolean(live.online ?? person.online);
        const selected = activePeer && activePeer.employeeCode === person.employeeCode;
        const typing = Boolean(typingBy[person.employeeCode]);

        return (
            <button
                type="button"
                key={person.employeeCode}
                className={`chat-person${selected ? ' is-selected' : ''}`}
                onClick={() => open(person)}
            >
                <PresenceAvatar person={person} online={online} size={42} />
                <span className="chat-person-text">
                    <span className="chat-person-top">
                        <b>{person.employeeName}</b>
                        {person.lastMessageAt && <i>{listTime(person.lastMessageAt)}</i>}
                    </span>
                    <span className="chat-person-sub">
                        {typing
                            ? <em className="is-typing">typing…</em>
                            : (
                                <em>
                                    {person.lastMessage?.text || person.designation || person.employeeCode}
                                </em>
                            )}
                        {person.unread > 0 && <span className="chat-person-badge">{person.unread}</span>}
                    </span>
                    <span className="chat-person-meta">{lastActiveLabel(online, live.lastSeen ?? person.lastSeen)}</span>
                </span>
            </button>
        );
    };

    return (
        <div className="wh-chat">
            <div className="chat-head">
                <h5 className="title">Messages</h5>
                <div className="chat-head-right">
                    <span className={`chat-conn${connected ? ' is-live' : ''}`}>
                        <i />{connected ? 'Live' : 'Reconnecting…'}
                    </span>
                    {canManageTeams && (
                        <button type="button" className="chat-manage" onClick={() => setSetupOpen(true)}>
                            <TuneRoundedIcon /> Manage teams
                        </button>
                    )}
                </div>
            </div>

            <div className={`chat-shell${mobileShowsThread ? ' shows-thread' : ''}`}>
                {/* ---------------- left rail ---------------- */}
                <aside className="chat-rail">
                    <div className="chat-search">
                        <SearchRoundedIcon />
                        <input
                            type="text"
                            placeholder="Search people…"
                            value={term}
                            onChange={(e) => setTerm(e.target.value)}
                        />
                        {term && (
                            <button type="button" onClick={() => setTerm('')} aria-label="Clear search">
                                <CloseRoundedIcon />
                            </button>
                        )}
                    </div>

                    {!searchResults && (
                        <div className="chat-tabs">
                            <button
                                type="button"
                                className={`chat-tab${tab === 'teams' ? ' is-on' : ''}`}
                                onClick={() => { setTab('teams'); setOpenTeam(null); }}
                            >
                                Teams
                            </button>
                            <button
                                type="button"
                                className={`chat-tab${tab === 'recent' ? ' is-on' : ''}`}
                                onClick={() => setTab('recent')}
                            >
                                Recent
                            </button>
                        </div>
                    )}

                    <div className="chat-rail-body">
                        {/* the four teams */}
                        {!searchResults && tab === 'teams' && !openTeam && (
                            <div className="chat-teams">
                                {categories.map((category, index) => {
                                    const Icon = TEAM_ICONS[category.icon] || GroupsRoundedIcon;
                                    return (
                                        <button
                                            type="button"
                                            key={category.key}
                                            className="chat-team"
                                            style={{ animationDelay: `${index * 55}ms` }}
                                            onClick={() => chooseTeam(category)}
                                        >
                                            <span className={`chat-team-icon is-${category.key}`}><Icon /></span>
                                            <span className="chat-team-text">
                                                <b>{category.label}</b>
                                                <em>{category.description}</em>
                                            </span>
                                            <span className="chat-team-count">{category.memberCount}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* the people in one team */}
                        {!searchResults && tab === 'teams' && openTeam && (
                            <>
                                <button type="button" className="chat-back" onClick={() => setOpenTeam(null)}>
                                    <ArrowBackRoundedIcon /> {openTeam.label}
                                </button>
                                {loadingMembers && <p className="chat-note">Loading…</p>}
                                {!loadingMembers && !members.length && (
                                    <p className="chat-note">
                                        Nobody is on this team yet.
                                        {canManageTeams
                                            ? ' Use “Manage teams” to add people.'
                                            : ' Ask an admin to set it up.'}
                                    </p>
                                )}
                                {members.map(renderPerson)}
                            </>
                        )}

                        {/* recent conversations */}
                        {!searchResults && tab === 'recent' && (
                            <>
                                {!rows.length && <p className="chat-note">No conversations yet. Pick a team to start one.</p>}
                                {rows.map(renderPerson)}
                            </>
                        )}

                        {/* search */}
                        {searchResults && (
                            <>
                                {!searchResults.length && <p className="chat-note">Nobody matched “{term}”.</p>}
                                {searchResults.map(renderPerson)}
                            </>
                        )}
                    </div>
                </aside>

                {/* ---------------- conversation ---------------- */}
                <section className="chat-main">
                    {!active && (
                        <div className="chat-blank">
                            <span className="chat-blank-mark"><ForumRoundedIcon /></span>
                            <h6>Pick a team, then a person</h6>
                            <p>
                                Message the HR, Accounts, IT, Engineering or Admin team directly.
                                You will see when they are online.
                            </p>
                        </div>
                    )}

                    {active && (
                        <>
                            <header className="chat-main-head">
                                <button
                                    type="button"
                                    className="chat-main-back"
                                    onClick={() => setMobileShowsThread(false)}
                                    aria-label="Back to list"
                                >
                                    <ArrowBackRoundedIcon />
                                </button>
                                <PresenceAvatar person={activePeer} online={activeOnline} size={42} />
                                <div className="chat-main-who">
                                    <b>{activePeer.employeeName}</b>
                                    <em>
                                        {typingBy[activePeer.employeeCode]
                                            ? 'typing…'
                                            : lastActiveLabel(activeOnline, activeLive.lastSeen ?? activePeer.lastSeen)}
                                    </em>
                                </div>
                                {activePeer.designation && (
                                    <span className="chat-main-role">{activePeer.designation}</span>
                                )}
                            </header>

                            <ChatThread conversationId={active.conversationId} peer={activePeer} />
                        </>
                    )}
                </section>
            </div>

            <TeamSetupDialog
                open={setupOpen}
                onClose={() => setSetupOpen(false)}
                onSaved={() => { loadCategories(); if (openTeam) loadMembers(openTeam.key); }}
            />
        </div>
    );
};

export default Chat;
